//this file responsible for 3 sound(plop, click, buzzer) cases.

import { ATaskConfiguration, ITaskConfiguration } from './TaskDefinitions';
import { Zoomer } from '../../../enums/ZoomerSettings';
import { MovingTargetTask, TimelineTask } from './MovingTargetTask';
import CONSTANTS from '../../../CONSTANTS/CONSTANTS';
import TaskScene from '../scene/TaskScene';

export class AudioTaskConfiguration extends ATaskConfiguration {
  filename: string = 'clicks';
  volume: number = CONSTANTS.audio.baseVolume;
  click: boolean = false;
  plop: boolean = false;
  zoomer: Zoomer = Zoomer.off;
  resetClick: boolean = false;
  resetZoomer: boolean = false;

  constructor(seed: number) {
    super(seed);
  }

  Copy(): AudioTaskConfiguration {
    let config = new AudioTaskConfiguration(this.seed);
    config.enabled = this.enabled;
    config.filename = this.filename;
    config.volume = this.volume;
    config.click = this.click;
    config.plop = this.plop;
    config.zoomer = this.zoomer;
    config.resetClick = this.resetClick;
    config.resetZoomer = this.resetZoomer;

    return config;
  }

  ApplyDifficulty(difficultyConfig: ITaskConfiguration): void {
    return;
  }
}

export class AudioTask extends TimelineTask<AudioTaskConfiguration> {
  public static readonly CLICKS_TIMELINE = 'clicks';
  public static readonly BUZZER_TIMELINE = 'buzzer';

  // Timeline controls at what interval the audio plays back.
  playback?: Phaser.Tweens.Timeline;
  movingTarget?: MovingTargetTask;
  clicks?: Phaser.Sound.BaseSound;
  plop?: Phaser.Sound.BaseSound;
  buzzer?: Phaser.Sound.BaseSound;

  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  constructor(movingTarget: MovingTargetTask, configuration: AudioTaskConfiguration, scene: Phaser.Scene) {
    super(configuration, scene, 'AudioTask');
    this.movingTarget = movingTarget;
  }

  Create(configuration: AudioTaskConfiguration, scene: Phaser.Scene): void {
    this.clicks = scene.sound.add('clicks');
    this.plop = scene.sound.add('plop');
    this.buzzer = scene.sound.add('buzzer');

    this.OnUpdate(configuration, this.configuration, scene, true);
  }

  CreatePlopSound(scene: Phaser.Scene) {
    let ts = scene as TaskScene;

    if (this.isActive && ts.calledBy === 'client')
      if (this.configuration.plop) {
        this.plop?.play();
      }
  }

  OnSetVisible(visible: boolean) {}

  OnUpdate(
    oldConfiguration: AudioTaskConfiguration,
    configuration: AudioTaskConfiguration,
    scene: Phaser.Scene,
    firstCreateCall: boolean
  ) {
    let ts = scene as TaskScene;

    if (ts.calledBy === 'client') {
      if (configuration.enabled) {
        let clicksTimeline = configuration.click
          ? this.createClick(configuration, scene)
          : scene.tweens.createTimeline();
        this.ReplaceTimeline(AudioTask.CLICKS_TIMELINE, clicksTimeline);

        let buzzerTimeline = this.createZoomer(configuration, scene);
        this.ReplaceTimeline(AudioTask.BUZZER_TIMELINE, buzzerTimeline);
      } else {
        this.StopTimelines(scene);
        this.clicks?.pause();
        this.buzzer?.pause();
      }
    }
  }

  private createZoomer(configuration: AudioTaskConfiguration, scene: Phaser.Scene): Phaser.Tweens.Timeline {
    switch (Number(configuration.zoomer)) {
      case CONSTANTS.audio.ZoomerCases.off:
        return this.createTimelineZoomer(configuration, scene, 0);
      case CONSTANTS.audio.ZoomerCases.interval_5_seconds:
        return this.createTimelineZoomer(configuration, scene, 5000);
      case CONSTANTS.audio.ZoomerCases.interval_10_seconds:
        return this.createTimelineZoomer(configuration, scene, 10000);

      default: {
        return this.createTimelineZoomer(configuration, scene, 0);
      }
    }
  }

  private createTimelineZoomer(configuration: AudioTaskConfiguration, scene: Phaser.Scene, duration: number) {
    if (duration === 0) {
      let timeline = scene.tweens.createTimeline();

      return timeline;
    }

    let timeline = scene.tweens.createTimeline();

    let dummy = { t: 0 };
    timeline.add({
      targets: dummy,
      t: { from: 0, to: 1 },
      duration: duration,
      repeat: -1,
      onRepeat: () => {
        if (!this.buzzer) return;
        this.buzzer.play();
      },
    });

    return timeline;
  }

  private createClick(configuration: AudioTaskConfiguration, scene: Phaser.Scene) {
    let timeline = scene.tweens.createTimeline();
    let dummy = { t: 0 };
    let perSecond = CONSTANTS.audio.clickPerSecond;

    let duration = CONSTANTS.audio.clickDuration;

    timeline.add({
      targets: dummy,
      t: { from: 0, to: 1 },
      duration: duration,
      repeat: -1,

      onRepeat: tween => {
        duration = this.random.between(CONSTANTS.audio.minClickDuration, CONSTANTS.audio.maxClickDuration);
        let pan = this.random.pickAValue(-1, 1);
        if (typeof pan === 'boolean') return;

        tween.setTimeScale(duration / perSecond);
        this.clicks?.play({ pan: pan });
      },
    });

    return timeline;
  }
}
